import { Box, Divider, Stack, Typography } from '@mui/material';
import { compact, first, sortBy } from 'lodash';

import { CheckIcon } from '@/components/icons/CheckIcon';
import { DataIcon } from '@/components/icons/DataIcon';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { DiagramDisplayCard } from '@/components/layout/DiagramDisplayCard/DiagramDisplayCard';
import { isAIGeneratedBundleKind } from '@/modules/presentation/clientPresentation/components/AddBundleMenu/AddBundleMenu.catalog';
import { usePresentationStringFormatter } from '@/modules/presentation/clientPresentation/presentationString/usePresentationStringFormatter';
import { getPresentationTemplateName } from '@/modules/presentationTemplates/presentationTemplate.utils';
import {
  getTemplateBundleLabel,
  getTemplateBundleLabelPageInput,
} from '@/modules/presentationTemplates/PresentationTemplateEditor/stages/presentationTemplate/PresentationTemplateStageEditor.utils';
import { THEME_SHADOWS } from '@/styles/themes/common';
import { COLORS } from '@/styles/tokens/colors';
import { getNodes } from '@/utils/graphqlUtils';

import { OnboardingPresentationTemplate } from './PresentationTemplateSelectionStep.utils';

const MAX_VISIBLE_SLIDES = 14;

function getSlideItems(
  template: OnboardingPresentationTemplate,
  format: (value: string) => string
) {
  return sortBy(
    compact(template.bundles.edges?.map((edge) => edge?.node) ?? []),
    'index'
  ).map((bundle) => {
    const firstPage = first(getNodes(bundle.pages));
    // Cover and custom bundles are single-page bundles, so prompt metadata is
    // always on their first page.
    const hasPrompt = Boolean(
      firstPage?.customPage?.systemPromptKey ||
        firstPage?.customPage?.customPrompt ||
        firstPage?.coverPage?.systemPromptKey ||
        firstPage?.coverPage?.customPrompt
    );
    return {
      label: format(
        getTemplateBundleLabel({
          kind: bundle.kind,
          ...getTemplateBundleLabelPageInput(firstPage),
          documentSummaryTemplateBundleConfiguration:
            bundle.documentSummaryTemplateBundleConfiguration,
        })
      ),
      isAIGenerated: isAIGeneratedBundleKind(bundle.kind) || hasPrompt,
    };
  });
}

interface TemplateCardProps {
  template: OnboardingPresentationTemplate;
  isSelected: boolean;
  onToggle: () => void;
}

export function TemplateCard({
  template,
  isSelected,
  onToggle,
}: TemplateCardProps) {
  const { plainFormatter } = usePresentationStringFormatter();
  const slideItems = getSlideItems(template, plainFormatter);
  const visibleSlideCount =
    slideItems.length > MAX_VISIBLE_SLIDES
      ? MAX_VISIBLE_SLIDES - 1
      : MAX_VISIBLE_SLIDES;
  const visibleSlides = slideItems.slice(0, visibleSlideCount);
  const overflowCount = slideItems.length - visibleSlides.length;

  return (
    <DiagramDisplayCard
      ariaLabel={`${getPresentationTemplateName(template)}, ${slideItems.length} slides`}
      bodySx={{ p: 2, width: '100%' }}
      onClick={onToggle}
      selected={isSelected}
      value={template.id}
      sx={{
        width: '100%',
        border: `1px solid ${isSelected ? COLORS.BRAND.NAVY : COLORS.GRAY[200]}`,
        borderRadius: '6px',
        boxShadow: isSelected ? THEME_SHADOWS.xs : 'none',
        '&:hover': {
          borderColor: isSelected ? COLORS.GRAY[800] : COLORS.GRAY[300],
          boxShadow: THEME_SHADOWS.xs,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="title-s">
          {getPresentationTemplateName(template)}
        </Typography>
        <Box
          aria-hidden
          data-testid={
            isSelected ? 'selected-template-icon' : 'unselected-template-icon'
          }
          sx={{
            alignItems: 'center',
            bgcolor: isSelected ? COLORS.BRAND.NAVY : 'transparent',
            border: `1px solid ${isSelected ? COLORS.BRAND.NAVY : COLORS.GRAY[300]}`,
            borderRadius: '2px',
            display: 'flex',
            height: 18,
            justifyContent: 'center',
            width: 18,
          }}
        >
          {isSelected && (
            <CheckIcon size={14} color={COLORS.PRIMITIVES.WHITE} />
          )}
        </Box>
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" justifyContent="space-between" mb={1.5}>
        <Typography variant="eyebrow-xs">Includes</Typography>
        <Typography variant="body-xs" sx={{ color: COLORS.TEXT.SECONDARY }}>
          {slideItems.length} slides
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gridTemplateRows: `repeat(${Math.max(1, Math.ceil(visibleSlides.length / 2))}, auto)`,
          gridAutoFlow: 'column',
          columnGap: 2,
          rowGap: 1,
        }}
      >
        {visibleSlides.map(({ label, isAIGenerated }, index) => (
          <Stack
            key={`${label}-${index}`}
            direction="row"
            alignItems="center"
            spacing={0.75}
          >
            {isAIGenerated ? (
              <Stars01Icon
                data-testid="ai-generated-slide-icon"
                size={14}
                color={COLORS.NAVY[300]}
              />
            ) : (
              <DataIcon
                data-testid="data-slide-icon"
                size={14}
                color={COLORS.NAVY[300]}
              />
            )}
            <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
              {label}
            </Typography>
          </Stack>
        ))}
        {overflowCount > 0 && (
          <Typography variant="body-xs" sx={{ color: COLORS.TEXT.SECONDARY }}>
            and {overflowCount} more slides…
          </Typography>
        )}
      </Box>
    </DiagramDisplayCard>
  );
}
